import React, { createContext, useContext, useEffect, useState } from 'react';
import { KnowledgeItem } from '../types/learning';
import * as knowledgeItemService from '../services/storage/knowledgeItemService';
import * as spacedRepetitionService from '../services/learning/spacedRepetitionService';
import * as learningMetricsService from '../services/learning/learningMetricsService';
import * as dailyTrainingService from '../services/learning/dailyTrainingService';

/**
 * Context type for learning state and services.
 * Provides read-only access to all key learning state and exposes learning-related services.
 */
export interface LearningContextType {
  /** All knowledge items tracked by the learning system */
  knowledgeItems: KnowledgeItem[];
  /** Loading state for knowledge items */
  isLoading: boolean;
  /** Error state for loading knowledge items */
  error: Error | null;
  /** The current daily training session, if generated */
  dailySession: KnowledgeItem[] | null;
  /** Items due for review (review queue) */
  reviewQueue: KnowledgeItem[];
  /** Service for CRUD operations on knowledge items */
  knowledgeItemService: typeof knowledgeItemService;
  /** Service for spaced repetition (SM-2) logic */
  spacedRepetitionService: typeof spacedRepetitionService;
  /** Service for learning metrics and analytics */
  learningMetricsService: typeof learningMetricsService;
  /** Service for generating daily training sessions */
  dailyTrainingService: typeof dailyTrainingService;
}

/**
 * React context for learning state and services.
 * Must be used within a LearningProvider.
 */
const LearningContext = createContext<LearningContextType | undefined>(undefined);

/**
 * Provider component for LearningContext.
 * Loads all knowledge items and provides learning state/services to consumers.
 */
export const LearningProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [knowledgeItems, setKnowledgeItems] = useState<KnowledgeItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [dailySession, setDailySession] = useState<KnowledgeItem[] | null>(null);

  useEffect(() => {
    setIsLoading(true);
    try {
      const items = knowledgeItemService.getAllKnowledgeItems();
      setKnowledgeItems(items);
      setDailySession(dailyTrainingService.generateDailySession(items));
      setIsLoading(false);
    } catch (e) {
      setError(e as Error);
      setIsLoading(false);
    }
  }, []);

  const reviewQueue = knowledgeItems.filter(
    item => item.nextReviewDate && new Date(item.nextReviewDate) <= new Date()
  );

  return (
    <LearningContext.Provider
      value={{
        knowledgeItems,
        isLoading,
        error,
        dailySession,
        reviewQueue,
        knowledgeItemService,
        spacedRepetitionService,
        learningMetricsService,
        dailyTrainingService,
      }}
    >
      {children}
    </LearningContext.Provider>
  );
};

/**
 * Hook to access the LearningContext.
 * Throws if used outside a LearningProvider.
 */
export const useLearning = () => {
  const ctx = useContext(LearningContext);
  if (!ctx) throw new Error('useLearning must be used within a LearningProvider');
  return ctx;
}; 